import { Suspense, useCallback, useEffect, useId, useRef, useState } from 'react';
import type { RefObject } from 'react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import api from '../../lib/api';
import { LazyEmojiPicker } from '../ui/LazyEmojiPicker';

export interface ReactionGroup {
  emoji: string;
  count: number;
  userIds: string[];
}

interface Props {
  messageId: string;
  reactions: ReactionGroup[];
  /** userId → displayName for pill tooltips; omitted when member data is unavailable. */
  memberNames?: Record<string, string>;
  /**
   * Increment to open the picker from outside (hover toolbar / context menu
   * in MessageItem). 0 / undefined means "no external request yet".
   */
  openPickerSignal?: number;
  /**
   * Where keyboard focus goes when a picker opened via `openPickerSignal`
   * is dismissed with Escape. For a message with no reactions this bar
   * renders nothing once the picker closes, so its own "+" button cannot
   * take the focus back — the hover-toolbar button that opened it can.
   */
  returnFocusTo?: RefObject<HTMLElement>;
}

export const QUICK_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '🚀'];

/** Pure toggle so the optimistic state and the socket patch agree exactly. */
export function applyReactionToggle(
  groups: ReactionGroup[],
  emoji: string,
  userId: string,
  kind: 'add' | 'remove',
): ReactionGroup[] {
  const next = groups.map((r) => ({ ...r, userIds: [...r.userIds] }));
  const idx = next.findIndex((r) => r.emoji === emoji);
  if (kind === 'add') {
    if (idx >= 0) {
      if (!next[idx].userIds.includes(userId)) {
        next[idx].userIds.push(userId);
        next[idx].count = next[idx].userIds.length;
      }
    } else {
      next.push({ emoji, count: 1, userIds: [userId] });
    }
  } else if (idx >= 0) {
    next[idx].userIds = next[idx].userIds.filter((u) => u !== userId);
    next[idx].count = next[idx].userIds.length;
    if (next[idx].count === 0) next.splice(idx, 1);
  }
  return next;
}

export function describeReactors(
  group: ReactionGroup,
  currentUserId: string | undefined,
  memberNames: Record<string, string> | undefined,
): string {
  const names: string[] = [];
  let unknown = 0;
  for (const id of group.userIds) {
    if (id === currentUserId) names.push('You');
    else if (memberNames?.[id]) names.push(memberNames[id]);
    else unknown += 1;
  }
  if (names.length === 0) {
    return `${group.count} ${group.count === 1 ? 'reaction' : 'reactions'}`;
  }
  const others = unknown > 0 ? ` and ${unknown} ${unknown === 1 ? 'other' : 'others'}` : '';
  return `${names.join(', ')}${others} reacted with ${group.emoji}`;
}

export default function ReactionBar({
  messageId,
  reactions,
  memberNames,
  openPickerSignal,
  returnFocusTo,
}: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [showPicker, setShowPicker] = useState(false);
  const [showFullPicker, setShowFullPicker] = useState(false);
  // Optimistic view of the reactions while a toggle is in flight. The socket
  // reaction:add/remove patch (applied to the query cache by MessageList) is
  // authoritative: whenever the `reactions` prop changes we drop the local
  // guess and show the server's state.
  const [optimistic, setOptimistic] = useState<ReactionGroup[] | null>(null);
  const queuesRef = useRef(new Map<string, Promise<void>>());
  const popoverRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  // True while the open picker was requested from outside (toolbar / context
  // menu) rather than by clicking "+", so Escape can send focus back there.
  const openedExternallyRef = useRef(false);
  const pickerId = useId();

  useEffect(() => {
    setOptimistic(null);
  }, [reactions]);

  useEffect(() => {
    if (openPickerSignal) {
      openedExternallyRef.current = true;
      setShowPicker(true);
    }
  }, [openPickerSignal]);

  const displayed = optimistic ?? reactions;
  const hasPills = displayed.length > 0;

  const closePicker = useCallback(
    (restoreFocus = false) => {
      setShowPicker(false);
      setShowFullPicker(false);
      const openedExternally = openedExternallyRef.current;
      openedExternallyRef.current = false;
      if (!restoreFocus) return;
      // "+" only survives the close when there are pills to show it next to;
      // otherwise the whole bar unmounts and focus would drop to <body>.
      const external = returnFocusTo?.current ?? null;
      const target =
        openedExternally && external ? external : hasPills ? addButtonRef.current : external;
      target?.focus();
    },
    [hasPills, returnFocusTo],
  );

  useEffect(() => {
    if (!showPicker) return;
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || addButtonRef.current?.contains(target)) return;
      closePicker();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closePicker(true);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showPicker, closePicker]);

  async function toggleReaction(emoji: string) {
    closePicker();
    if (!currentUserId) {
      toast.error('Still loading your profile — try again in a moment.');
      return;
    }
    const existing = displayed.find((r) => r.emoji === emoji);
    const hasReacted = !!existing?.userIds.includes(currentUserId);
    const kind = hasReacted ? 'remove' : 'add';
    setOptimistic(applyReactionToggle(displayed, emoji, currentUserId, kind));

    const execute = async () => {
      try {
        if (hasReacted) {
          await api.delete(`/api/v1/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
        } else {
          await api.post(`/api/v1/messages/${messageId}/reactions`, { emoji });
        }
      } catch {
        setOptimistic(null);
        toast.error(hasReacted ? 'Could not remove your reaction' : 'Could not add your reaction');
      }
    };
    // Requests for one emoji run strictly in click order. A quick double click
    // used to fire the add and the remove concurrently; they could reach the
    // API in the opposite order and leave the reaction added although the
    // second click removed it. The first request starts immediately.
    const previous = queuesRef.current.get(emoji);
    const run = previous ? previous.then(execute) : execute();
    queuesRef.current.set(emoji, run);
    void run.then(() => {
      if (queuesRef.current.get(emoji) === run) queuesRef.current.delete(emoji);
    });
    await run;
  }

  // Nothing to show: no reactions and no open picker. The "Add reaction"
  // affordance for such messages lives in MessageItem's hover toolbar and
  // context menu, which open this picker via `openPickerSignal`.
  if (displayed.length === 0 && !showPicker) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {displayed.map((r) => {
        const mine = !!currentUserId && r.userIds.includes(currentUserId);
        return (
          <button
            key={r.emoji}
            type="button"
            onClick={() => toggleReaction(r.emoji)}
            aria-pressed={mine}
            aria-label={`${r.emoji} ${r.count}${mine ? ', you reacted' : ''}`}
            title={describeReactors(r, currentUserId, memberNames)}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition ${
              mine
                ? 'border-primary bg-primary-light text-primary shadow-warm-sm'
                : 'border-warm-border-light bg-warm-gray-50 text-warm-gray-600 hover:bg-warm-gray-100 hover:border-warm-border'
            }`}
          >
            <span aria-hidden="true">{r.emoji}</span>
            <span className="font-medium">{r.count}</span>
          </button>
        );
      })}

      <div className="relative">
        <button
          ref={addButtonRef}
          type="button"
          onClick={() => {
            if (showPicker) {
              closePicker();
            } else {
              openedExternallyRef.current = false;
              setShowPicker(true);
            }
          }}
          aria-label="Add reaction"
          aria-haspopup="dialog"
          aria-expanded={showPicker}
          aria-controls={showPicker ? pickerId : undefined}
          title="Add reaction"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-warm-border-light text-xs text-warm-gray-400 hover:bg-warm-gray-100 hover:text-primary hover:border-primary/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/50"
        >
          +
        </button>
        {showPicker && (
          <div
            ref={popoverRef}
            id={pickerId}
            role="dialog"
            aria-label="Add reaction"
            className="absolute bottom-8 left-0 z-20 rounded-xl border border-warm-border-light bg-warm-white p-2 shadow-warm-md"
          >
            <div className="flex gap-1">
              {QUICK_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => toggleReaction(emoji)}
                  aria-label={`React with ${emoji}`}
                  className="rounded-lg p-1 text-lg hover:bg-warm-gray-100"
                >
                  {emoji}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowFullPicker((v) => !v)}
                aria-expanded={showFullPicker}
                aria-label="More emojis"
                title="More emojis"
                className="rounded-lg px-2 text-xs font-medium text-warm-gray-500 hover:bg-warm-gray-100 hover:text-primary"
              >
                More…
              </button>
            </div>
            {showFullPicker && (
              <div className="mt-2">
                <Suspense
                  fallback={
                    <div className="flex h-24 w-64 items-center justify-center">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  }
                >
                  <LazyEmojiPicker onEmojiSelect={(e) => toggleReaction(e.native)} />
                </Suspense>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
